/**
 * The hero's arrival: a white-out, and the mark coming through it.
 *
 * The page opens fogged white. The brand mark resolves out of that fog,
 * takes a shine across its face, then expands past the reader — and the
 * name it leaves behind is the page's own. No burst and no colour: the
 * light here is the page's own white and the mark's own seafoam.
 *
 * The mark is an HTML layer, so its growth and its softness are plain
 * CSS; the shine inside it moves as an SVG attribute, so nothing depends
 * on where a transform's origin falls.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';
import { HOUSE_H, HOUSE_PATH, HOUSE_W } from '@components/wee/house-mark';
import { EASE_OUT_EXPO, FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { colors } from '@config/colors';

/**
 * The arrival, in seconds from load. The words follow the mark: the
 * name arrives as it passes the reader, and each line after it.
 */
export const CUE = {
  /** The fog holds, and the mark begins to resolve in it. */
  mark: 0.35,
  /** Light crosses the mark's face, before it starts to come forward. */
  shine: 1.05,
  headline: 2.0,
  tagline: 2.35,
  ctas: 2.7,
} as const;

/** How long a word line takes to arrive. */
const WORD_S = 0.9;

/** The art's box, with the mark centred in it. */
const BOX_W = 240;
const BOX_H = 220;
const INK_X = (BOX_W - HOUSE_W) / 2;
const INK_Y = (BOX_H - HOUSE_H) / 2;

const ID = 'hero-intro';
const url = (suffix: string) => `url(#${ID}-${suffix})`;

/** Reduced motion, and the screenshot suite, get the hero already arrived. */
function useSettled(): boolean {
  const reduced = useReducedMotion() ?? false;
  return reduced || FREEZE_FOR_CAPTURE;
}

type RevealProps = {
  /** When this line arrives, in seconds from load. */
  at: number;
  children: React.ReactNode;
};

/** One line of the hero, rising into place as the mark passes. */
export const HeroReveal: React.FC<RevealProps> = ({ at, children }) => {
  if (useSettled()) return <>{children}</>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: WORD_S, ease: EASE_OUT_EXPO, delay: at }}
    >
      {children}
    </motion.div>
  );
};

export const HeroIntro: React.FC = () => {
  if (useSettled()) return null;

  return (
    <>
      {/* The white-out the page opens in, thinning to nothing. */}
      <Fog
        aria-hidden="true"
        initial={{ opacity: 0.95 }}
        animate={{ opacity: [0.95, 0.9, 0.4, 0] }}
        transition={{ duration: 2.2, times: [0, 0.2, 0.62, 1], ease: 'easeInOut' }}
      />

      <Stage aria-hidden="true">
        {/* Out of the fog, then past the reader: one movement that
            resolves, holds while the shine crosses it, and goes. */}
        <Mark
          initial={{ opacity: 0, scale: 0.88, filter: 'blur(16px)' }}
          animate={{
            opacity: [0, 1, 1, 0.5, 0],
            scale: [0.88, 1, 1.04, 2.6, 8],
            filter: ['blur(16px)', 'blur(0px)', 'blur(0px)', 'blur(4px)', 'blur(16px)'],
          }}
          transition={{
            duration: 2.0,
            // It clears the words before they arrive: gone by 2.35s, and
            // the name comes in at 2.0s.
            times: [0, 0.3, 0.55, 0.78, 1],
            delay: CUE.mark,
            ease: [EASE_OUT_EXPO, 'linear', 'easeIn', 'easeIn'],
          }}
        >
          <svg viewBox={`0 0 ${BOX_W} ${BOX_H}`} focusable="false">
            <defs>
              {/* Deep seafoam: the mark has to read against the white it
                  arrives through as much as the water it leaves into. */}
              <linearGradient id={`${ID}-fill`} x1="0" y1="0" x2="0.35" y2="1">
                <stop offset="0" stopColor={colors.brand.exxxtraDark} />
                <stop offset="0.5" stopColor={colors.brand.exxtraDark} />
                <stop offset="1" stopColor={colors.brand.exxxtraDark} />
              </linearGradient>
              {/* Brighter than the app's own shimmer: this band crosses a
                  deep seafoam mark, not a gold one. */}
              {/* Straight across the band: the tilt comes from the group
                  that carries it, and any vertical run here would put the
                  bright part of the gradient off the mark entirely. */}
              <linearGradient id={`${ID}-shine`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor={colors.wee.markShimmerClear} />
                <stop offset="0.35" stopColor={colors.wee.markShimmer} />
                <stop offset="0.5" stopColor={colors.wee.lanternCore} />
                <stop offset="0.65" stopColor={colors.wee.markShimmer} />
                <stop offset="1" stopColor={colors.wee.markShimmerClear} />
              </linearGradient>
              <clipPath id={`${ID}-clip`}>
                <path d={HOUSE_PATH} transform={`translate(${INK_X} ${INK_Y})`} />
              </clipPath>
            </defs>
            <g transform={`translate(${INK_X} ${INK_Y})`}>
              <path d={HOUSE_PATH} fill={url('fill')} />
            </g>
            {/* The shine: a band of light crossing the mark's face. */}
            <g clipPath={url('clip')}>
              <g transform={`rotate(-18 ${BOX_W / 2} ${BOX_H / 2})`}>
                {/* `attrX`, not `x`: on an SVG element, motion's `x` is a
                    transform, and a transform here would fight the
                    rotation this band is carried by. */}
                <motion.rect
                  y={-BOX_H}
                  width={HOUSE_W * 0.55}
                  height={BOX_H * 3}
                  fill={url('shine')}
                  initial={{ attrX: -HOUSE_W, opacity: 0 }}
                  animate={{ attrX: [-HOUSE_W, BOX_W], opacity: [0, 1, 1, 0] }}
                  transition={{
                    duration: 0.6,
                    times: [0, 0.15, 0.7, 1],
                    delay: CUE.shine,
                    ease: 'easeInOut',
                  }}
                />
              </g>
            </g>
          </svg>
        </Mark>
      </Stage>
    </>
  );
};

/* The white-out covers the window — the water and the hero both. The
   nav, which the page is handing itself over to, stays above it. */
const Fog = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(
    circle at 50% 46%,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.97) 45%,
    rgba(240, 252, 249, 0.9) 100%
  );
`;

/* Over the words, not in their flow: the mark passes where they are
   about to be, and leaves no gap behind when it is gone. */
const Stage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(360px, 62vw);
  aspect-ratio: ${BOX_W} / ${BOX_H};
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`;

const Mark = styled(motion.div)`
  position: absolute;
  inset: 0;
`;
